import { create } from 'zustand';

/**
 * Snapshot-mode store — global flag + envelope describing the snapshot
 * the user is currently *viewing*.
 *
 * Why this is global:
 *   - The snapshot banner (`<SnapshotBanner />`) is mounted in the app
 *     shell so it can appear above any route. Any route that displays a
 *     frozen snapshot needs to flip this on, and unrelated routes need to
 *     keep it off.
 *   - ADR-009 makes snapshots write-once, and the banner is the user-
 *     visible signal that the view is read-only. Centralizing the toggle
 *     keeps that contract single-sourced — a route can't accidentally show
 *     stale "live" UI while the user is actually looking at a snapshot.
 *
 * Who calls this:
 *   - The snapshot-detail routes (`/engagements/:id/snapshots/:snap_id/*`)
 *     — owned by T-0101 — will call `enterSnapshotMode(...)` on mount and
 *     `exitSnapshotMode()` on unmount, ideally inside a `useEffect`
 *     cleanup so route-swapping always tears the banner down.
 *   - This ticket (T-0095) only publishes the store + banner; route
 *     wiring belongs to the consumers.
 *
 * Why Zustand (not React context):
 *   - Matches the existing project convention (auth store uses zustand).
 *   - Non-hook readers (e.g. utilities that need to know "is the app in
 *     snapshot mode" outside React) can hit `useSnapshotMode.getState()`
 *     the same way `getAuthToken()` does.
 */

/**
 * Snapshot kinds the platform produces. Mirrors the enum the snapshot-
 * capture service uses on the backend so a route can pass the kind
 * through verbatim without translation.
 *
 *   - `pulse`            — contract-pulse snapshot (E06-F07)
 *   - `readiness`        — go-live readiness scorecard snapshot (E08-F06)
 *   - `health`           — health-check synthesis snapshot (E07-F08)
 *   - `manual`           — user-initiated capture on the overview hero
 *   - `client_viewer`    — frozen projection shown through the magic-link
 *                          viewer (E01-F06); banner copy is identical but
 *                          analytics can disambiguate.
 */
export type SnapshotKind = 'pulse' | 'readiness' | 'health' | 'manual' | 'client_viewer';

/**
 * The minimum envelope the banner needs to render its message. Routes can
 * pass through the snapshot record's fields directly — the field names
 * mirror the snapshot table.
 */
export interface SnapshotEnvelope {
  /** Snapshot row id (UUID). Carried so consumers can deep-link. */
  snapshot_id: string;
  /** ISO timestamp the snapshot was captured (server time). */
  captured_at: string;
  /** Display name of the consultant who captured the snapshot. */
  captured_by: string;
  /** Snapshot kind — see {@link SnapshotKind}. */
  kind: SnapshotKind;
}

interface SnapshotModeState {
  /** Active envelope when the user is viewing a snapshot; `null` otherwise. */
  active: SnapshotEnvelope | null;
  /** Flip the banner on with the given envelope. Idempotent — re-entering with
   *  the same snapshot is a no-op. */
  enterSnapshotMode: (envelope: SnapshotEnvelope) => void;
  /** Flip the banner off. Safe to call when no snapshot is active. */
  exitSnapshotMode: () => void;
}

/**
 * Hook + non-hook reader. Usage:
 *
 *   // In a route component:
 *   const enter = useSnapshotMode((s) => s.enterSnapshotMode);
 *   const exit = useSnapshotMode((s) => s.exitSnapshotMode);
 *   useEffect(() => {
 *     enter({ snapshot_id, captured_at, captured_by, kind });
 *     return () => exit();
 *   }, [snapshot_id]);
 *
 *   // In the banner component:
 *   const active = useSnapshotMode((s) => s.active);
 *
 *   // Outside React (rare):
 *   useSnapshotMode.getState().active
 */
export const useSnapshotMode = create<SnapshotModeState>((set) => ({
  active: null,
  enterSnapshotMode: (envelope) =>
    set((state) => {
      // Idempotent re-entry: if the same snapshot is already active, skip
      // the set to avoid a no-op re-render of every subscriber.
      if (state.active && state.active.snapshot_id === envelope.snapshot_id) {
        return state;
      }
      return { active: envelope };
    }),
  exitSnapshotMode: () => set((state) => (state.active === null ? state : { active: null })),
}));
